import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { GetStaticProps, GetStaticPaths } from 'next'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { Typography, Breadcrumbs, Link, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import Review from '../../src/components/Review/SavingsAccount'
import KeyFacts from '../../src/components/Product/earn/KeyFacts'
import RatingBreakdown from '../../src/components/Product/earn/RatingBreakdown'

import { SavingAccount as ISavingAccount } from '../../types/savingAccount'
import { urls } from '../../src/utils/config'
import Nav from '../../src/components/Nav'
import Footer from '../../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  root: {
    margin: 'auto',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.4) 13%, #FFFFFF 100%);',
    overflow: 'auto',
  },
  contentContainer: {
    margin: 'auto',
    maxWidth: '1200px',
    padding: '0 20px ',
  },
  navSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > a': {
      fontSize: '14px',
      textTransform: 'none',
      borderRadius: '.6rem',
    },
  },
  breadCrumbs: {
    padding: '.5rem .5rem .5rem 1.5rem',
    position: 'relative',
    fontSize: '.8rem',
    '&::before': {
      position: 'absolute',
      content: '""',
      height: '100%',
      top: 0,
      left: 0,
      width: '0.5rem',
      background: theme.palette.primary.main,
      borderRadius: '0.3rem',
    },
  },
  p: {
    fontSize: '.8rem',
    fontWeight: 'bold',
  },
  factsBreakdownWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    columnGap: '1rem',
    rowGap: '1rem',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr',
      columnGap: '1rem',
      rowGap: '1rem',
    },
  },
  currentPriceContainer: {
    margin: '4rem 0',
  },
}))

interface Props {
  savingAccount?: ISavingAccount
}

function SavingAccount({ savingAccount }: Props): JSX.Element {
  const classes = useStyles({})
  const { pathname } = useRouter()
  const url = `${urls.savingAccounts}/${savingAccount?.path_id}`
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  if (!savingAccount) {
    return <div>Not Found</div>
  }

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: url,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={savingAccount?.seo_title}
            description={savingAccount?.seo_description}
            openGraph={{
              url: `https://${process.env.NEXT_PUBLIC_APP_URL}/${url}`,
              title: savingAccount?.seo_title,
              description: savingAccount?.seo_description,
              images: savingAccount?.sharing_image && [
                {
                  url: savingAccount?.sharing_image.url,
                  width: 1200,
                  height: 630,
                  alt: `${savingAccount?.name} image - Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.contentContainer}>
            <div className={classes.navSection}>
              <Breadcrumbs separator="->" aria-label="breadcrumb" className={classes.breadCrumbs}>
                <Link color="inherit" href={urls.productEarn}>
                  Products
                </Link>
                <Link color="inherit" href={urls.savingAccounts}>
                  Savings Accounts
                </Link>
                <Typography className={classes.p} color="textPrimary">
                  {savingAccount.name}
                </Typography>
              </Breadcrumbs>
              <Button color="primary" variant="contained" href={savingAccount.url} target="_blank">
                Give {savingAccount.name} a try
              </Button>
            </div>
            <div className={classes.factsBreakdownWrapper}>
              <KeyFacts savingAccount={savingAccount} />
              <RatingBreakdown savingAccount={savingAccount} />
            </div>
          </div>
          <Review {...savingAccount} />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_URL}/saving-accounts`)
  const data = (await response.json()) as ISavingAccount[]
  const preRenderedSavingAccounts = data.map((savingAccount) => ({
    params: { id: savingAccount.path_id },
  }))
  return {
    paths: preRenderedSavingAccounts,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const id = context.params?.id
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_STRAPI_URL}/saving-accounts?path_id=${id}`,
  )
  const data = (await response.json()) as ISavingAccount[]
  const savingAccount = data[0]
  return {
    props: {
      savingAccount,
    },
  }
}

export default SavingAccount
